import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Briefcase, DollarSign, TrendingUp, Target, Package,
  Clock, FileText, ArrowUpDown, ChevronDown, ChevronUp,
  ArrowLeft, Download, Loader2, ClipboardList, Receipt, X,
} from 'lucide-react';
import { reportsAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { JobStatusBadge, cleanJobNumber } from '../components/JobStatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { SortableTableHead } from '../components/ui/SortableTableHead';

/* ────────── helpers ────────── */

function fmt$(v) { return v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'; }
function fmtPct(v) { return v != null ? `${Number(v).toFixed(1)}%` : '—'; }
function fmtInt(v) { return v != null ? Number(v).toLocaleString() : '—'; }

function defaultDateRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

/* ────────── KPI Card ────────── */

function KpiCard({ label, value, icon: Icon, sub }) {
  return (
    <Card data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <CardContent className="pt-5 pb-4 px-5 flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-semibold tracking-tight">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ────────── Skeleton row ────────── */

function SkeletonRows() {
  return (
    <div className="space-y-2 animate-pulse py-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <div className="w-32 h-4 bg-muted rounded" />
          <div className="w-16 h-4 bg-muted rounded" />
          <div className="w-20 h-4 bg-muted rounded" />
          <div className="w-20 h-4 bg-muted rounded" />
          <div className="w-16 h-4 bg-muted rounded" />
          <div className="w-16 h-4 bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}

/* ────────── Sort header ────────── */

function SortHeader({ label, field, sortField, sortDir, onSort, className = '' }) {
  const active = sortField === field;
  return (
    <button
      className={`flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors ${className}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      {label}
      {active ? (sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
    </button>
  );
}

/* ────────── Detail Modal ────────── */

function TechDetailModal({ techId, dateFrom, dateTo, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('jobs');
  const navigate = useNavigate();

  useEffect(() => {
    if (!techId) return;
    setLoading(true);
    reportsAPI.scorecardDetail(techId, { date_from: dateFrom, date_to: dateTo })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [techId, dateFrom, dateTo]);

  if (!techId) return null;

  const t = data?.technician;
  const jobs = data?.recent_jobs || [];
  const invoices = data?.recent_invoices || [];
  const estimates = data?.recent_estimates || [];
  const mats = data?.material_totals || {};

  return (
    <Dialog open={!!techId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tech-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {loading ? 'Loading...' : t?.technician_name || 'Technician'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Completed</p>
                <p className="text-lg font-semibold">{fmtInt(t?.jobs_completed)}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Revenue</p>
                <p className="text-lg font-semibold">{fmt$(t?.revenue)}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Avg Ticket</p>
                <p className="text-lg font-semibold">{fmt$(t?.avg_ticket)}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Close Rate</p>
                <p className="text-lg font-semibold">{fmtPct(t?.close_rate)}</p>
              </div>
            </div>

            {/* Profitability KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="profitability-kpis">
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Labor Cost</p>
                <p className="text-lg font-semibold">{fmt$(t?.labor_cost)}</p>
                <p className="text-[10px] text-muted-foreground">{t?.labor_source === 'actual' ? 'Actual Labor Time' : t?.labor_source === 'scheduled' ? 'Scheduled Duration' : 'N/A'}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Direct Cost</p>
                <p className="text-lg font-semibold">{fmt$(t?.total_direct_cost)}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Gross Profit</p>
                <p className={`text-lg font-semibold ${(t?.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt$(t?.gross_profit)}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg">
                <p className="text-[11px] text-muted-foreground">Gross Margin</p>
                <p className={`text-lg font-semibold ${(t?.gross_margin_pct || 0) >= 50 ? 'text-green-600' : (t?.gross_margin_pct || 0) >= 25 ? 'text-amber-600' : 'text-red-600'}`}>{t?.gross_margin_pct || 0}%</p>
              </div>
            </div>

            {/* Material totals */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                  <Package className="w-4 h-4" /> Material Totals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-xs text-muted-foreground">Items Used</p><p className="font-medium">{fmtInt(mats.count)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Material Cost</p><p className="font-medium">{fmt$(mats.cost)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Material Revenue</p><p className="font-medium">{fmt$(mats.revenue)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Material Margin</p><p className="font-medium">{fmt$(mats.margin)}</p></div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs: Jobs / Invoices / Estimates */}
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-8">
                <TabsTrigger value="jobs" className="text-xs h-7 px-3" data-testid="detail-tab-jobs">Jobs ({jobs.length})</TabsTrigger>
                <TabsTrigger value="invoices" className="text-xs h-7 px-3" data-testid="detail-tab-invoices">Invoices ({invoices.length})</TabsTrigger>
                <TabsTrigger value="estimates" className="text-xs h-7 px-3" data-testid="detail-tab-estimates">Estimates ({estimates.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="jobs" className="mt-3">
                {jobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No jobs in this period.</p>
                ) : (
                  <div className="space-y-1">
                    {jobs.map(j => (
                      <div key={j.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/40 cursor-pointer" onClick={() => { onClose(); navigate(`/jobs/${j.id}`); }} data-testid={`detail-job-${j.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-sm font-medium">#{cleanJobNumber(j.job_number)}</span>
                          <span className="text-sm text-muted-foreground truncate">{j.customer_name || j.job_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{fmt$(j.amount)}</span>
                          <JobStatusBadge status={j.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invoices" className="mt-3">
                {invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No invoices in this period.</p>
                ) : (
                  <div className="space-y-1">
                    {invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/40 cursor-pointer" onClick={() => { onClose(); navigate(`/invoices/${inv.id}`); }} data-testid={`detail-inv-${inv.id}`}>
                        <span className="text-sm font-medium">{inv.invoice_number}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm">{fmt$(inv.total)}</span>
                          <Badge variant={inv.paid ? 'default' : 'secondary'} className={`text-xs ${inv.paid ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                            {inv.paid ? 'Paid' : `Due ${fmt$(inv.balance_due)}`}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="estimates" className="mt-3">
                {estimates.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No estimates in this period.</p>
                ) : (
                  <div className="space-y-1">
                    {estimates.map(est => (
                      <div key={est.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/40 cursor-pointer" onClick={() => { onClose(); navigate(`/estimates/${est.id}`); }} data-testid={`detail-est-${est.id}`}>
                        <span className="text-sm font-medium">{est.estimate_number}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm">{fmt$(est.total)}</span>
                          <Badge variant={est.approved ? 'default' : 'secondary'} className={`text-xs ${est.approved ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : ''}`}>
                            {est.approved ? 'Approved' : 'Pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ────────── CSV Export ────────── */

function exportCSV(technicians, summary, dateFrom, dateTo) {
  const headers = [
    'Technician', 'Jobs Completed', 'Jobs Scheduled', 'Revenue', 'Avg Ticket',
    'Labor Cost', 'Material Cost', 'Total Direct Cost', 'Gross Profit', 'Gross Margin %',
    'Estimates Sold', 'Close Rate %', 'Invoices Paid', 'Invoices Unpaid',
    'Materials Used', 'Material Revenue', 'Material Margin',
    'Labor Source', 'Labor Minutes',
  ];
  const rows = technicians.map(t => [
    t.technician_name, t.jobs_completed, t.jobs_scheduled, t.revenue, t.avg_ticket,
    t.labor_cost, t.material_cost, t.total_direct_cost, t.gross_profit, t.gross_margin_pct,
    t.estimates_sold, t.close_rate, t.invoices_paid, t.invoices_unpaid,
    t.materials_used_count, t.material_revenue, t.material_margin,
    t.labor_source, t.labor_minutes,
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `technician-scorecards-${dateFrom}-to-${dateTo}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ────────── Leaderboard Tab ────────── */

const RANK_METRICS = [
  { value: 'revenue', label: 'Revenue', fmt: fmt$ },
  { value: 'gross_profit', label: 'Gross Profit', fmt: fmt$ },
  { value: 'gross_margin_pct', label: 'Gross Margin %', fmt: (v) => `${v}%` },
  { value: 'jobs_completed', label: 'Jobs Completed', fmt: fmtInt },
  { value: 'avg_ticket', label: 'Avg Ticket', fmt: fmt$ },
  { value: 'close_rate', label: 'Close Rate', fmt: fmtPct },
  { value: 'material_margin', label: 'Material Margin', fmt: fmt$ },
];

function rankIcon(rank) {
  if (rank === 1) return '\u{1F947}';
  if (rank === 2) return '\u{1F948}';
  if (rank === 3) return '\u{1F949}';
  return rank;
}

function LeaderboardTable({ technicians, rankBy, onRankChange, currentUserId }) {
  const sorted = [...technicians].sort((a, b) => {
    const av = a[rankBy] ?? 0;
    const bv = b[rankBy] ?? 0;
    return bv - av;
  });

  const activeMeta = RANK_METRICS.find(m => m.value === rankBy);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-xs text-muted-foreground">Rank by</label>
        <Select value={rankBy} onValueChange={onRankChange}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="leaderboard-rank-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANK_METRICS.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card data-testid="leaderboard-table">
        <CardContent className="p-0">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center" data-testid="leaderboard-empty-state">
              <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium mb-1">No technician data found</p>
              <p className="text-xs text-muted-foreground max-w-[320px]">
                Scorecards will appear once technicians are assigned to jobs via dispatch or direct assignment. Try expanding your date range.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leaderboard-rank-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-center px-3 py-2.5 w-16"><span className="text-xs font-medium text-muted-foreground">Rank</span></th>
                    <th className="text-left px-4 py-2.5"><span className="text-xs font-medium text-muted-foreground">Technician</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'revenue' ? 'text-foreground' : 'text-muted-foreground'}`}>Revenue</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'gross_profit' ? 'text-foreground' : 'text-muted-foreground'}`}>Gross Profit</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'gross_margin_pct' ? 'text-foreground' : 'text-muted-foreground'}`}>GM %</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'jobs_completed' ? 'text-foreground' : 'text-muted-foreground'}`}>Jobs</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'avg_ticket' ? 'text-foreground' : 'text-muted-foreground'}`}>Avg Ticket</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'close_rate' ? 'text-foreground' : 'text-muted-foreground'}`}>Close Rate</span></th>
                    <th className="text-right px-3 py-2.5"><span className={`text-xs font-medium ${rankBy === 'material_margin' ? 'text-foreground' : 'text-muted-foreground'}`}>Material Margin</span></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((t, i) => {
                    const rank = i + 1;
                    const isMedal = rank <= 3;
                    const isMe = currentUserId && t.technician_id === currentUserId;
                    return (
                      <tr key={t.technician_id} className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${isMe ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''}`} data-testid={`leaderboard-row-${t.technician_id}`}>
                        <td className={`text-center px-3 py-3 ${isMedal ? 'text-lg' : 'tabular-nums text-muted-foreground'}`}>{rankIcon(rank)}</td>
                        <td className="px-4 py-3 font-medium">
                          {t.technician_name}
                          {isMe && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid="you-badge">You</span>}
                        </td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'revenue' ? 'font-semibold' : ''}`}>{fmt$(t.revenue)}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'gross_profit' ? 'font-semibold' : ''} ${t.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{t.revenue > 0 ? fmt$(t.gross_profit) : <span className="text-muted-foreground">&mdash;</span>}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'gross_margin_pct' ? 'font-semibold' : ''} ${t.gross_margin_pct >= 50 ? 'text-green-600' : t.gross_margin_pct >= 25 ? 'text-amber-600' : 'text-red-600'}`}>{t.revenue > 0 ? `${t.gross_margin_pct}%` : <span className="text-muted-foreground">&mdash;</span>}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'jobs_completed' ? 'font-semibold' : ''}`}>{fmtInt(t.jobs_completed)}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'avg_ticket' ? 'font-semibold' : ''}`}>{fmt$(t.avg_ticket)}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'close_rate' ? 'font-semibold' : ''}`}>{t.estimates_total > 0 ? fmtPct(t.close_rate) : <span className="text-muted-foreground">&mdash;</span>}</td>
                        <td className={`text-right px-3 py-3 tabular-nums ${rankBy === 'material_margin' ? 'font-semibold' : ''}`}>{t.material_cost > 0 || t.material_revenue > 0 ? fmt$(t.material_margin) : <span className="text-muted-foreground">&mdash;</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════
   Main Page Component
   ════════════════════════════════════════ */

export default function TechnicianScorecards() {
  const defaults = defaultDateRange();
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [techFilter, setTechFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortField, setSortField] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [detailTechId, setDetailTechId] = useState(null);
  const [techOptions, setTechOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('scorecards');
  const [rankBy, setRankBy] = useState('revenue');
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  // Load technician filter options
  useEffect(() => {
    reportsAPI.getTechnicians().then(r => setTechOptions(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (techFilter) params.technician_id = techFilter;
      const res = await reportsAPI.scorecards(params);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, techFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const summary = data?.summary || {};
  const technicians = [...(data?.technicians || [])].sort((a, b) => {
    const av = a[sortField] ?? 0;
    const bv = b[sortField] ?? 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="technician-scorecards-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Technician Scorecards</h1>
            <p className="text-sm text-muted-foreground mt-1">Performance metrics by technician for the selected period</p>
          </div>
          {technicians.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => exportCSV(technicians, summary, dateFrom, dateTo)} data-testid="export-csv-btn" className="hidden sm:inline-flex">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card data-testid="scorecard-filters">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex gap-2 items-end">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">From</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[140px] text-sm" data-testid="filter-date-from" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">To</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[140px] text-sm" data-testid="filter-date-to" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Technician</label>
                <Select value={techFilter} onValueChange={v => setTechFilter(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="filter-technician">
                    <SelectValue placeholder="All technicians" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All technicians</SelectItem>
                    {techOptions.map(t => (
                      <SelectItem key={t.value || t.id} value={t.value || t.id}>{t.label || t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary KPIs */}
        {!loading && data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-kpis">
            <KpiCard label="Technicians" value={fmtInt(summary.total_technicians)} icon={Users} />
            <KpiCard label="Total Revenue" value={fmt$(summary.total_revenue)} icon={DollarSign} />
            <KpiCard label="Gross Profit" value={fmt$(summary.total_gross_profit)} icon={TrendingUp} />
            <KpiCard label="Avg Margin" value={`${summary.avg_gross_margin_pct}%`} icon={Target} />
          </div>
        )}

        {/* View Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-9" data-testid="view-tabs">
            <TabsTrigger value="scorecards" className="text-sm px-4" data-testid="tab-scorecards">Scorecards</TabsTrigger>
            <TabsTrigger value="leaderboard" className="text-sm px-4" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
          </TabsList>

          {/* Scorecards Tab */}
          <TabsContent value="scorecards" className="mt-4">
            <Card data-testid="technician-table">
              <CardContent className="p-0">
                {loading ? (
                  <SkeletonRows />
                ) : technicians.length === 0 ? (
                  <div className="flex flex-col items-center py-16 text-center" data-testid="scorecards-empty-state">
                    <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <p className="text-sm font-medium mb-1">No technician data found</p>
                    <p className="text-xs text-muted-foreground max-w-[320px]">
                      Scorecards will appear once technicians are assigned to jobs via dispatch or direct assignment. Try expanding your date range.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed" data-testid="scorecards-table">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="technician_name" onSort={handleSort} className="text-left px-4 py-2.5 min-w-[140px]">Technician</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="jobs_completed" onSort={handleSort} className="text-right px-3 py-2.5 w-[85px]">Completed</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="jobs_scheduled" onSort={handleSort} className="text-right px-3 py-2.5 w-[85px]">Scheduled</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="revenue" onSort={handleSort} className="text-right px-3 py-2.5 w-[100px]">Revenue</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="avg_ticket" onSort={handleSort} className="text-right px-3 py-2.5 w-[95px]">Avg Ticket</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="close_rate" onSort={handleSort} className="text-right px-3 py-2.5 w-[100px]">Close Rate</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="material_margin" onSort={handleSort} className="text-right px-3 py-2.5 w-[110px]">Mat. Margin</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="labor_cost" onSort={handleSort} className="text-right px-3 py-2.5 w-[95px]">Labor Cost</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="gross_profit" onSort={handleSort} className="text-right px-3 py-2.5 w-[100px]">Gross Profit</SortableTableHead>
                          <SortableTableHead sortKey={sortField} sortDir={sortDir} columnKey="gross_margin_pct" onSort={handleSort} className="text-right px-3 py-2.5 w-[70px]">GM %</SortableTableHead>
                          <th className="text-right px-3 py-2.5 w-[85px]"><span className="text-xs font-medium text-muted-foreground">Invoices</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {technicians.map(t => {
                          const isMe = currentUserId && t.technician_id === currentUserId;
                          return (
                          <tr
                            key={t.technician_id}
                            className={`border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors ${isMe ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''}`}
                            onClick={() => setDetailTechId(t.technician_id)}
                            data-testid={`tech-row-${t.technician_id}`}
                          >
                            <td className="px-4 py-3 font-medium min-w-[140px]">
                              {t.technician_name}
                              {isMe && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid="you-badge">You</span>}
                            </td>
                            <td className="text-right px-3 py-3 tabular-nums w-[85px]">{fmtInt(t.jobs_completed)}</td>
                            <td className="text-right px-3 py-3 tabular-nums text-muted-foreground w-[85px]">{fmtInt(t.jobs_scheduled)}</td>
                            <td className="text-right px-3 py-3 tabular-nums font-medium w-[100px]">{fmt$(t.revenue)}</td>
                            <td className="text-right px-3 py-3 tabular-nums w-[95px]">{fmt$(t.avg_ticket)}</td>
                            <td className="text-right px-3 py-3 tabular-nums w-[100px]">
                              {t.estimates_total > 0 ? (
                                <span>{fmtPct(t.close_rate)} <span className="text-muted-foreground text-xs">({t.estimates_sold}/{t.estimates_total})</span></span>
                              ) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="text-right px-3 py-3 tabular-nums w-[110px]">{t.material_cost > 0 || t.material_revenue > 0 ? fmt$(t.material_margin) : <span className="text-muted-foreground">—</span>}</td>
                            <td className="text-right px-3 py-3 tabular-nums w-[95px]">
                              {t.labor_cost > 0 ? fmt$(t.labor_cost) : <span className="text-muted-foreground">—</span>}
                              {t.labor_source === 'scheduled' && t.labor_cost > 0 && <span className="text-[9px] text-amber-500 ml-1" title="Based on scheduled duration">est</span>}
                            </td>
                            <td className={`text-right px-3 py-3 tabular-nums font-medium w-[100px] ${t.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {t.revenue > 0 ? fmt$(t.gross_profit) : <span className="text-muted-foreground font-normal">—</span>}
                            </td>
                            <td className={`text-right px-3 py-3 tabular-nums w-[70px] ${t.gross_margin_pct >= 50 ? 'text-green-600' : t.gross_margin_pct >= 25 ? 'text-amber-600' : 'text-red-600'}`}>
                              {t.revenue > 0 ? `${t.gross_margin_pct}%` : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="text-right px-3 py-3 tabular-nums w-[85px]">
                              {(t.invoices_paid > 0 || t.invoices_unpaid > 0) ? (
                                <span>{t.invoices_paid} <span className="text-muted-foreground">/ {t.invoices_paid + t.invoices_unpaid}</span></span>
                              ) : <span className="text-muted-foreground">—</span>}
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="mt-4">
            {loading ? <SkeletonRows /> : (
              <LeaderboardTable technicians={data?.technicians || []} rankBy={rankBy} onRankChange={setRankBy} currentUserId={currentUserId} />
            )}
          </TabsContent>
        </Tabs>

        {/* Drill-in Modal */}
        <TechDetailModal
          techId={detailTechId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setDetailTechId(null)}
        />
      </div>
    </Layout>
  );
}
